import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, FileCheck, Link2, RefreshCw, MoreHorizontal, 
  ChevronDown, Lock, Crown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import QuickActionModal from './QuickActionModal';

/**
 * CockpitQuickActions - Standardized quick-actions toolbar for dashboards
 * Used in Executive, PMO/Portfolio, and Project Manager views
 * 
 * Props:
 * - contextType: 'portfolio' | 'program' | 'project' | 'executive'
 * - contextId: optional ID for preselecting entity
 * - contextName: optional name for display
 * - onRefresh: callback to trigger data refresh
 * - refreshing: boolean for refresh button state
 * - className: additional CSS classes
 */
const CockpitQuickActions = ({
  contextType = 'executive',
  contextId = null,
  contextName = null,
  onRefresh,
  refreshing = false,
  className = ''
}) => {
  const { user } = useAuth();
  const [quickActionModal, setQuickActionModal] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);

  const tenantId = user?.tenant?.id || user?.tenant_id;
  const tenantTier = user?.tenant?.subscription?.tier || 'growth';
  
  // Get user permissions
  const userRole = user?.role || user?.roles?.[0]?.role_key || user?.roles?.[0]?.key || 'viewer';
  const userPermissions = {
    canComment: ['sysadmin', 'pmo_power', 'program_admin', 'pm', 'steerco', 'executive', 'portfolio_manager', 'team_lead', 'contributor'].includes(userRole),
    canDecision: ['sysadmin', 'pmo_power', 'program_admin', 'pm', 'steerco', 'executive', 'portfolio_manager'].includes(userRole),
    canDependency: ['sysadmin', 'pmo_power', 'program_admin', 'pm', 'steerco', 'executive', 'portfolio_manager', 'team_lead'].includes(userRole),
  };

  // Tier gating
  const tierCapabilities = {
    growth: { comment: true, decision: false, dependency: false },
    business: { comment: true, decision: true, dependency: true },
    enterprise: { comment: true, decision: true, dependency: true },
  };
  const tierCaps = tierCapabilities[tenantTier] || tierCapabilities.growth;

  // Combined visibility (RBAC + tier)
  const canShowComment = userPermissions.canComment && tierCaps.comment;
  const canShowDecision = userPermissions.canDecision && tierCaps.decision;
  const canShowDependency = userPermissions.canDependency && tierCaps.dependency;

  // Responsive handling
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show dropdown below 1200px
  const showAsDropdown = windowWidth < 1200;

  // Preselected entity for modals
  const getPreselectedEntity = () => {
    if (!contextId || !contextName) return null;
    
    const typeMap = {
      'portfolio': 'portfolio',
      'program': 'program',
      'project': 'project',
      'executive': null
    };
    
    if (!typeMap[contextType]) return null;
    
    return {
      id: contextId,
      name: contextName,
      type: typeMap[contextType]
    };
  };

  // Telemetry
  const trackAction = (action) => {
    try {
      api.trackEvent?.({
        event: `click_${action}`,
        context: contextType,
        contextId
      });
    } catch (e) {
      // Silent fail for telemetry
    }
  };

  const handleActionClick = (action) => {
    trackAction(action);
    setQuickActionModal(action);
    setShowDropdown(false);
  };

  const handleRefresh = () => {
    trackAction('refresh');
    if (onRefresh) onRefresh();
  };

  // Action button component
  const ActionButton = ({ action, icon: Icon, label, colorClass, disabled, tooltip }) => {
    const isDisabled = disabled;
    
    return (
      <button
        onClick={() => !isDisabled && handleActionClick(action)}
        disabled={isDisabled}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
          ${isDisabled 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : `${colorClass} hover:opacity-90`
          }`}
        title={tooltip || label}
        data-testid={`quick-${action}`}
      >
        {isDisabled ? <Lock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        <span className="whitespace-nowrap">{label}</span>
      </button>
    );
  };

  // Dropdown item component
  const DropdownItem = ({ action, icon: Icon, label, colorClass, disabled, tooltip }) => {
    const isDisabled = disabled;
    
    return (
      <button
        onClick={() => !isDisabled && handleActionClick(action)}
        disabled={isDisabled}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors
          ${isDisabled 
            ? 'text-gray-400 cursor-not-allowed bg-gray-50' 
            : 'text-gray-700 hover:bg-gray-50'
          }`}
        title={tooltip}
        data-testid={`quick-${action}-dropdown`}
      >
        {isDisabled ? (
          <Lock className="w-4 h-4 text-gray-400" />
        ) : (
          <div className={`p-1.5 rounded ${colorClass.replace('text-white', 'text-white').replace('bg-', 'bg-opacity-20 bg-')}`}>
            <Icon className={`w-4 h-4 ${colorClass.includes('bg-[var(--clr-brand-primary)]') ? 'text-[var(--clr-brand-primary)]' : colorClass.includes('amber') ? 'text-amber-600' : 'text-purple-600'}`} />
          </div>
        )}
        <div className="flex-1">
          <span className="font-medium">{label}</span>
          {isDisabled && <span className="block text-xs text-gray-400 mt-0.5">{tooltip}</span>}
        </div>
      </button>
    );
  };

  // Actions configuration
  const actions = [
    {
      action: 'comment',
      icon: MessageSquare,
      label: 'Add Comment',
      colorClass: 'bg-[var(--clr-brand-primary)] text-white',
      disabled: !canShowComment,
      tooltip: !canShowComment 
        ? (tierCaps.comment ? 'Permission required' : 'Upgrade to Business tier')
        : 'Add a comment to any entity'
    },
    {
      action: 'decision',
      icon: FileCheck,
      label: 'Request Decision',
      colorClass: 'bg-amber-500 text-white',
      disabled: !canShowDecision,
      tooltip: !canShowDecision 
        ? (tierCaps.decision ? 'Permission required' : 'Upgrade to Business tier')
        : 'Request a decision from stakeholders'
    },
    {
      action: 'dependency',
      icon: Link2,
      label: 'Flag Dependency',
      colorClass: 'bg-purple-600 text-white',
      disabled: !canShowDependency,
      tooltip: !canShowDependency 
        ? (tierCaps.dependency ? 'Permission required' : 'Upgrade to Business tier')
        : 'Flag a blocking dependency'
    }
  ];

  // Filter to only show actions that are at least tier-visible
  const visibleActions = actions.filter(a => 
    (a.action === 'comment' && tierCaps.comment) ||
    (a.action === 'decision' && tierCaps.decision) ||
    (a.action === 'dependency' && tierCaps.dependency)
  );

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`} data-testid="cockpit-quick-actions">
        {showAsDropdown ? (
          /* Dropdown mode for narrow screens */
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
              data-testid="quick-actions-dropdown-toggle"
            >
              <MoreHorizontal className="w-4 h-4" />
              Actions
              <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowDropdown(false)}
                />
                <div 
                  className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50"
                  data-testid="quick-actions-dropdown-menu"
                >
                  {visibleActions.map(action => (
                    <DropdownItem key={action.action} {...action} />
                  ))}
                  
                  <div className="border-t border-gray-100 mt-2 pt-2 px-4">
                    <button
                      onClick={() => {
                        handleRefresh();
                        setShowDropdown(false);
                      }}
                      disabled={refreshing}
                      className="w-full flex items-center gap-3 py-2 text-sm text-gray-700 hover:text-gray-900"
                      data-testid="quick-refresh-dropdown"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                      <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                    </button>
                  </div>

                  {/* Tier upgrade hint for Growth */}
                  {tenantTier === 'growth' && (
                    <div className="border-t border-gray-100 mt-2 pt-2 px-4 pb-2">
                      <div className="flex items-center gap-2 text-xs text-amber-600">
                        <Crown className="w-3.5 h-3.5" />
                        <span>Upgrade to unlock more actions</span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          /* Button row mode for wide screens */
          <>
            {visibleActions.map(action => (
              <ActionButton key={action.action} {...action} />
            ))}
          </>
        )}

        {/* Refresh button - always visible in button mode */}
        {!showAsDropdown && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-sm"
            data-testid="quick-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Quick Action Modal */}
      <QuickActionModal
        isOpen={!!quickActionModal}
        onClose={() => setQuickActionModal(null)}
        actionType={quickActionModal}
        tenantId={tenantId}
        preselectedEntity={getPreselectedEntity()}
      />
    </>
  );
};

export default CockpitQuickActions;
